import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import '../../styles/bloomberg-theme.css';

// Minimum wallet value threshold for display and calculations
const MIN_WALLET_VALUE = 300;

interface WalletSummary {
  id: string;
  email: string;
  wallet_address: string;
  wallet_label: string;
  current_value: number;
  daily_change: number;
  daily_change_percent: number;
  mtd_change: number;
  mtd_change_percent: number;
  ytd_change: number;
  ytd_change_percent: number;
  twr_total: number;
  volatility: number;
  sharpe_ratio: number;
  status: 'active' | 'inactive' | 'alert';
}

interface PortfolioOverviewProps {
  onViewWallet?: (userId: string) => void;
}

interface PortfolioMetrics {
  total_aum: number;
  active_wallets: number;
  total_wallets: number;
  portfolio_pnl_today: number;
  portfolio_pnl_today_percent: number;
  portfolio_pnl_mtd: number;
  portfolio_pnl_mtd_percent: number;
  portfolio_pnl_ytd: number;
  portfolio_pnl_ytd_percent: number;
  average_twr: number;
  total_deposits_today: number;
  total_withdrawals_today: number;
  top_performer: {
    email: string;
    wallet: string;
    performance: number;
  };
  worst_performer: {
    email: string;
    wallet: string;
    performance: number;
  };
}

const PortfolioOverviewDashboard: React.FC<PortfolioOverviewProps> = ({ onViewWallet }) => {
  const { user } = useAuth();
  const [wallets, setWallets] = useState<WalletSummary[]>([]);
  const [metrics, setMetrics] = useState<PortfolioMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof WalletSummary>('current_value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());
  const [displayCount, setDisplayCount] = useState(100);

  // Helper function to calculate risk metrics from daily performance data
  const calculateUserRiskMetrics = (dailyData: any[]): { volatility: number; sharpeRatio: number } => {
    if (!dailyData || dailyData.length < 2) {
      return { volatility: 0, sharpeRatio: 0 };
    }

    // Extract daily returns (use up to 30 days for calculation)
    const dataPoints = dailyData.slice(0, Math.min(30, dailyData.length));
    const returns: number[] = [];

    for (const day of dataPoints) {
      if (day.daily_change_percent !== undefined && day.daily_change_percent !== null) {
        returns.push(day.daily_change_percent);
      }
    }

    if (returns.length < 2) {
      return { volatility: 0, sharpeRatio: 0 };
    }

    // Calculate average return
    const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;

    // Calculate variance and volatility (standard deviation)
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length;
    const volatilityDaily = Math.sqrt(variance);

    // Annualize volatility (252 trading days)
    const volatilityAnnualized = volatilityDaily * Math.sqrt(252);

    // Calculate Sharpe ratio
    // Using 5% annual risk-free rate
    const riskFreeRateAnnual = 0.05;
    const riskFreeRateDaily = riskFreeRateAnnual / 252;

    // Convert percentage to decimal for calculation
    const avgReturnDecimal = avgReturn / 100;
    const excessReturn = avgReturnDecimal - riskFreeRateDaily;

    // Sharpe ratio = (Return - Risk Free Rate) / Volatility
    const sharpeRatio = volatilityDaily > 0
      ? (excessReturn / (volatilityDaily / 100)) * Math.sqrt(252)
      : 0;

    return {
      volatility: volatilityAnnualized,
      sharpeRatio: sharpeRatio
    };
  };

  const fetchPortfolioData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [users, consolidatedPerf, consolidatedTWR, consolidatedDaily, cashFlows] = await Promise.all([
        apiService.getAdminUsers(),
        apiService.getConsolidatedPerformance(),
        apiService.getConsolidatedTWR(),
        apiService.getConsolidatedDailyPerformance(30, 0), // Get 30 days of data
        apiService.getCashFlows(100, 0) // Get recent cash flows
      ]);

      // Process each user's performance data
      const userPerformancePromises = users.map(async (user: any) => {
        try {
          // Fetch individual user performance and TWR
          const [userPerf, userTWR, userDaily] = await Promise.all([
            apiService.getAdminUserPerformance(user.id, 'all'),
            apiService.getAdminUserTWR(user.id),
            apiService.getAdminUserDailyPerformance(user.id, 30, 0)
          ]);

          // Calculate period metrics from daily data
          const today = new Date();
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          const yearStart = new Date(today.getFullYear(), 0, 1);

          // Get today's performance
          let dailyChange = 0;
          let dailyChangePercent = 0;
          if (userDaily.data && userDaily.data.length > 0) {
            dailyChange = userDaily.data[0].daily_change_usd || 0;
            dailyChangePercent = userDaily.data[0].daily_change_percent || 0;
          }

          // Calculate MTD and YTD
          let mtdChange = 0;
          let ytdChange = 0;
          let mtdStartValue = 0;
          let ytdStartValue = 0;

          for (const day of userDaily.data) {
            const dayDate = new Date(day.date);

            // MTD calculation
            if (dayDate >= monthStart) {
              mtdChange += day.daily_change_usd || 0;
              if (mtdStartValue === 0 && day.total_value_usd) {
                mtdStartValue = day.total_value_usd - day.daily_change_usd;
              }
            }

            // YTD calculation
            if (dayDate >= yearStart) {
              ytdChange += day.daily_change_usd || 0;
              if (ytdStartValue === 0 && day.total_value_usd) {
                ytdStartValue = day.total_value_usd - day.daily_change_usd;
              }
            }
          }

          const mtdChangePercent = mtdStartValue > 0 ? (mtdChange / mtdStartValue) * 100 : 0;
          const ytdChangePercent = ytdStartValue > 0 ? (ytdChange / ytdStartValue) * 100 : 0;

          // Calculate risk metrics from daily data
          const riskMetrics = calculateUserRiskMetrics(userDaily.data);

          // Determine status based on recent activity
          const lastActivity = userDaily.data[0]?.timestamp;
          const daysSinceActivity = lastActivity
            ? (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24)
            : 999;

          let status: 'active' | 'inactive' | 'alert' = 'active';
          if (daysSinceActivity > 7) status = 'inactive';
          if (dailyChangePercent < -10) status = 'alert'; // Alert if daily loss > 10%

          return {
            id: user.id,
            email: user.email,
            wallet_address: user.wallet_address || 'N/A',
            wallet_label: user.wallet_label || user.email.split('@')[0],
            current_value: userPerf.current_value || user.current_value || 0,
            daily_change: dailyChange,
            daily_change_percent: dailyChangePercent,
            mtd_change: mtdChange,
            mtd_change_percent: mtdChangePercent,
            ytd_change: ytdChange,
            ytd_change_percent: ytdChangePercent,
            twr_total: userTWR?.twr?.total || 0,
            volatility: riskMetrics.volatility,
            sharpe_ratio: riskMetrics.sharpeRatio,
            status: status
          };
        } catch (error) {
          console.error(`Error fetching data for user ${user.id}:`, error);
          // Return user with default values if individual fetch fails
          return {
            id: user.id,
            email: user.email,
            wallet_address: user.wallet_address || 'N/A',
            wallet_label: user.wallet_label || user.email.split('@')[0],
            current_value: user.current_value || 0,
            daily_change: 0,
            daily_change_percent: 0,
            mtd_change: 0,
            mtd_change_percent: 0,
            ytd_change: 0,
            ytd_change_percent: 0,
            twr_total: 0,
            volatility: 0,
            sharpe_ratio: 0,
            status: 'inactive' as const
          };
        }
      });

      const allPortfolioData = await Promise.all(userPerformancePromises);
      // Filter out wallets below minimum value threshold
      const portfolioData = allPortfolioData.filter(w => w.current_value > MIN_WALLET_VALUE && w.status !== 'inactive');
      setWallets(portfolioData);

      // Calculate portfolio metrics with proper null handling
      const totalAUM = portfolioData.reduce((sum, w) => {
        const value = Number(w.current_value) || 0;
        return sum + value;
      }, 0);

      const activeCount = portfolioData.filter(w => w.status === 'active').length;

      // Find real top and worst performers (exclude those with 0 change)
      const walletsWithData = portfolioData.filter(w => w.daily_change_percent !== 0);

      const topPerformer = walletsWithData.length > 0
        ? walletsWithData.reduce((best, current) =>
            current.daily_change_percent > best.daily_change_percent ? current : best
          )
        : portfolioData[0];

      const worstPerformer = walletsWithData.length > 0
        ? walletsWithData.reduce((worst, current) =>
            current.daily_change_percent < worst.daily_change_percent ? current : worst
          )
        : portfolioData[0];

      // Calculate today's cash flows from the API data
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todaysCashFlows = cashFlows.data.filter(cf => {
        const cfDate = new Date(cf.timestamp);
        cfDate.setHours(0, 0, 0, 0);
        return cfDate.getTime() === today.getTime();
      });

      const totalDepositsToday = todaysCashFlows
        .filter(cf => cf.type === 'deposit')
        .reduce((sum, cf) => sum + (cf.amount_usd || 0), 0);

      const totalWithdrawalsToday = todaysCashFlows
        .filter(cf => cf.type === 'withdrawal')
        .reduce((sum, cf) => sum + Math.abs(cf.amount_usd || 0), 0);

      setMetrics({
        total_aum: totalAUM,
        active_wallets: activeCount,
        total_wallets: portfolioData.length,
        portfolio_pnl_today: portfolioData.reduce((sum, w) => sum + (w.daily_change || 0), 0),
        portfolio_pnl_today_percent: portfolioData.length > 0
          ? portfolioData.reduce((sum, w) => sum + (w.daily_change_percent || 0), 0) / portfolioData.length
          : 0,
        portfolio_pnl_mtd: portfolioData.reduce((sum, w) => sum + (w.mtd_change || 0), 0),
        portfolio_pnl_mtd_percent: portfolioData.length > 0
          ? portfolioData.reduce((sum, w) => sum + (w.mtd_change_percent || 0), 0) / portfolioData.length
          : 0,
        portfolio_pnl_ytd: portfolioData.reduce((sum, w) => sum + (w.ytd_change || 0), 0),
        portfolio_pnl_ytd_percent: portfolioData.length > 0
          ? portfolioData.reduce((sum, w) => sum + (w.ytd_change_percent || 0), 0) / portfolioData.length
          : 0,
        average_twr: consolidatedTWR?.twr?.total || 0,
        total_deposits_today: totalDepositsToday,
        total_withdrawals_today: totalWithdrawalsToday,
        top_performer: {
          email: topPerformer?.email || 'N/A',
          wallet: topPerformer?.wallet_address || 'N/A',
          performance: topPerformer?.daily_change_percent || 0
        },
        worst_performer: {
          email: worstPerformer?.email || 'N/A',
          wallet: worstPerformer?.wallet_address || 'N/A',
          performance: worstPerformer?.daily_change_percent || 0
        }
      });

      setError(null);
    } catch (err) {
      setError('FAILED TO LOAD PORTFOLIO DATA');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) {
      fetchPortfolioData();
      const interval = setInterval(fetchPortfolioData, 60000);
      return () => clearInterval(interval);
    }
  }, [fetchPortfolioData, user]);

  const formatCurrency = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue >= 1e6) {
      return `$${(value / 1e6).toFixed(2)}M`;
    } else if (absValue >= 1e3) {
      return `$${(value / 1e3).toFixed(2)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const handleSort = (column: keyof WalletSummary) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedWallets = [...wallets].sort((a, b) => {
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    const direction = sortDirection === 'asc' ? 1 : -1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * direction;
    }
    return String(aVal).localeCompare(String(bVal)) * direction;
  });

  if (!user?.is_admin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <span style={{ color: 'var(--bb-red)' }}>ADMIN ACCESS REQUIRED</span>
      </div>
    );
  }

  if (loading && !metrics) {
    return (
      <div className="bloomberg-terminal" style={{ padding: '40px', textAlign: 'center' }}>
        <span className="bb-blink" style={{ color: 'var(--bb-amber)' }}>LOADING PORTFOLIO DATA...</span>
      </div>
    );
  }

  return (
    <div className="bloomberg-terminal" style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{
          color: 'var(--bb-amber)',
          fontSize: '24px',
          fontWeight: 'bold',
          letterSpacing: '0.1em',
          marginBottom: '5px'
        }}>
          PORTFOLIO OVERVIEW
        </h1>
        <p style={{ color: 'var(--bb-text-secondary)', fontSize: '12px' }}>
          REAL-TIME PORTFOLIO MANAGEMENT DASHBOARD
        </p>
      </div>

      {error && (
        <div style={{
          padding: '10px',
          border: '1px solid var(--bb-red)',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <span style={{ color: 'var(--bb-red)' }}>ERROR: {error}</span>
        </div>
      )}

      {metrics && (
        <>
          {/* Key Metrics Grid */}
          <div className="bb-panel" style={{ marginBottom: '20px' }}>
            <div className="bb-header">
              <div className="bb-header-title">KEY PERFORMANCE INDICATORS</div>
              <div className="bb-header-info">
                <span style={{ fontSize: '10px', color: 'var(--bb-text-secondary)' }}>
                  LAST UPDATE: {new Date().toLocaleTimeString()}
                </span>
              </div>
            </div>
            <div className="bb-panel-content">
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1px',
                backgroundColor: 'var(--bb-border)'
              }}>
                {/* Total AUM */}
                <div style={{ backgroundColor: 'var(--bb-black)', padding: '15px' }}>
                  <div className="bb-data-label">TOTAL AUM</div>
                  <div className="bb-data-value" style={{ fontSize: '24px', color: 'var(--bb-amber)' }}>
                    {formatCurrency(metrics.total_aum)}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--bb-text-secondary)' }}>
                    {metrics.active_wallets}/{metrics.total_wallets} ACTIVE
                  </div>
                </div>

                {/* Today's P&L */}
                <div style={{ backgroundColor: 'var(--bb-black)', padding: '15px' }}>
                  <div className="bb-data-label">TODAY'S P&L</div>
                  <div className={metrics.portfolio_pnl_today >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                       style={{ fontSize: '20px' }}>
                    {formatCurrency(metrics.portfolio_pnl_today)}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {formatPercent(metrics.portfolio_pnl_today_percent)}
                  </div>
                </div>

                {/* MTD P&L */}
                <div style={{ backgroundColor: 'var(--bb-black)', padding: '15px' }}>
                  <div className="bb-data-label">MTD P&L</div>
                  <div className={metrics.portfolio_pnl_mtd >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                       style={{ fontSize: '20px' }}>
                    {formatCurrency(metrics.portfolio_pnl_mtd)}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {formatPercent(metrics.portfolio_pnl_mtd_percent)}
                  </div>
                </div>

                {/* YTD P&L */}
                <div style={{ backgroundColor: 'var(--bb-black)', padding: '15px' }}>
                  <div className="bb-data-label">YTD P&L</div>
                  <div className={metrics.portfolio_pnl_ytd >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                       style={{ fontSize: '20px' }}>
                    {formatCurrency(metrics.portfolio_pnl_ytd)}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {formatPercent(metrics.portfolio_pnl_ytd_percent)}
                  </div>
                </div>

                {/* Average TWR */}
                <div style={{ backgroundColor: 'var(--bb-black)', padding: '15px' }}>
                  <div className="bb-data-label">AVG TWR</div>
                  <div className={metrics.average_twr >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                       style={{ fontSize: '20px' }}>
                    {formatPercent(metrics.average_twr)}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--bb-text-secondary)' }}>
                    TRADING PERFORMANCE
                  </div>
                </div>

                {/* Cash Flows */}
                <div style={{ backgroundColor: 'var(--bb-black)', padding: '15px' }}>
                  <div className="bb-data-label">TODAY'S FLOWS</div>
                  <div style={{ fontSize: '14px' }}>
                    <div style={{ color: 'var(--bb-green)' }}>
                      ↑ {formatCurrency(metrics.total_deposits_today)}
                    </div>
                    <div style={{ color: 'var(--bb-red)' }}>
                      ↓ {formatCurrency(metrics.total_withdrawals_today)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Leaders */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div className="bb-panel">
              <div className="bb-panel-header" style={{ backgroundColor: 'rgba(0, 255, 0, 0.1)' }}>
                TOP PERFORMER TODAY
              </div>
              <div className="bb-panel-content" style={{ padding: '15px' }}>
                <div style={{ fontSize: '12px', color: 'var(--bb-text-secondary)' }}>
                  {metrics.top_performer.email}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--bb-gray)', marginBottom: '8px' }}>
                  {metrics.top_performer.wallet}
                </div>
                <div className="bb-value-positive" style={{ fontSize: '20px' }}>
                  {formatPercent(metrics.top_performer.performance)}
                </div>
              </div>
            </div>

            <div className="bb-panel">
              <div className="bb-panel-header" style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)' }}>
                WORST PERFORMER TODAY
              </div>
              <div className="bb-panel-content" style={{ padding: '15px' }}>
                <div style={{ fontSize: '12px', color: 'var(--bb-text-secondary)' }}>
                  {metrics.worst_performer.email}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--bb-gray)', marginBottom: '8px' }}>
                  {metrics.worst_performer.wallet}
                </div>
                <div className="bb-value-negative" style={{ fontSize: '20px' }}>
                  {formatPercent(metrics.worst_performer.performance)}
                </div>
              </div>
            </div>
          </div>

          {/* Multi-Wallet Performance Table */}
          <div className="bb-panel">
            <div className="bb-header">
              <div className="bb-header-title">WALLET PERFORMANCE MATRIX</div>
              <div className="bb-header-info">
                <button
                  className="bb-button"
                  style={{ padding: '4px 8px', fontSize: '10px', marginRight: '10px' }}
                  onClick={() => window.location.href = '#/admin/wallets'}
                >
                  VIEW DETAILS
                </button>
                <button
                  className="bb-button"
                  style={{ padding: '4px 8px', fontSize: '10px' }}
                >
                  EXPORT DATA
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="bb-table" style={{ width: '100%', fontSize: '11px', tableLayout: 'fixed' }}>
                <thead>
                  <tr className="bb-table-header">
                    <th className="bb-table-cell" style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedWallets(new Set(wallets.map(w => w.id)));
                          } else {
                            setSelectedWallets(new Set());
                          }
                        }}
                      />
                    </th>
                    <th className="bb-table-cell" onClick={() => handleSort('wallet_label')} style={{ cursor: 'pointer', width: '25%' }}>
                      WALLET {sortColumn === 'wallet_label' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-right" onClick={() => handleSort('current_value')} style={{ cursor: 'pointer', width: '120px', textAlign: 'right', paddingRight: '12px' }}>
                      VALUE {sortColumn === 'current_value' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-right" onClick={() => handleSort('daily_change_percent')} style={{ cursor: 'pointer', width: '80px', textAlign: 'right', paddingRight: '12px' }}>
                      TODAY {sortColumn === 'daily_change_percent' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-right" onClick={() => handleSort('mtd_change_percent')} style={{ cursor: 'pointer', width: '80px', textAlign: 'right', paddingRight: '12px' }}>
                      MTD {sortColumn === 'mtd_change_percent' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-right" onClick={() => handleSort('ytd_change_percent')} style={{ cursor: 'pointer', width: '80px', textAlign: 'right', paddingRight: '12px' }}>
                      YTD {sortColumn === 'ytd_change_percent' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-right" onClick={() => handleSort('twr_total')} style={{ cursor: 'pointer', width: '80px', textAlign: 'right', paddingRight: '12px' }}>
                      TWR {sortColumn === 'twr_total' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-right" onClick={() => handleSort('volatility')} style={{ cursor: 'pointer', width: '70px', textAlign: 'right', paddingRight: '12px' }}>
                      VOL {sortColumn === 'volatility' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-right" onClick={() => handleSort('sharpe_ratio')} style={{ cursor: 'pointer', width: '70px', textAlign: 'right', paddingRight: '12px' }}>
                      SHARPE {sortColumn === 'sharpe_ratio' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className="bb-table-cell text-center" style={{ width: '80px', textAlign: 'center' }}>STATUS</th>
                    <th className="bb-table-cell text-center" style={{ width: '80px', textAlign: 'center' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedWallets.slice(0, displayCount).map((wallet) =>  (
                    <tr key={wallet.id} className="bb-table-row">
                      <td className="bb-table-cell">
                        <input
                          type="checkbox"
                          checked={selectedWallets.has(wallet.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedWallets);
                            if (e.target.checked) {
                              newSelected.add(wallet.id);
                            } else {
                              newSelected.delete(wallet.id);
                            }
                            setSelectedWallets(newSelected);
                          }}
                        />
                      </td>
                      <td className="bb-table-cell">
                        <div>
                          <div style={{ color: 'var(--bb-text-data)' }}>{wallet.wallet_label}</div>
                          <div style={{ fontSize: '10px', color: 'var(--bb-gray)' }}>{wallet.email}</div>
                        </div>
                      </td>
                      <td className="bb-table-cell text-right mono-number" style={{ textAlign: 'right', paddingRight: '12px' }}>
                        {formatCurrency(wallet.current_value)}
                      </td>
                      <td className={`bb-table-cell text-right mono-number ${wallet.daily_change_percent >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`} style={{ textAlign: 'right', paddingRight: '12px' }}>
                        {formatPercent(wallet.daily_change_percent)}
                      </td>
                      <td className={`bb-table-cell text-right mono-number ${wallet.mtd_change_percent >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`} style={{ textAlign: 'right', paddingRight: '12px' }}>
                        {formatPercent(wallet.mtd_change_percent)}
                      </td>
                      <td className={`bb-table-cell text-right mono-number ${wallet.ytd_change_percent >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`} style={{ textAlign: 'right', paddingRight: '12px' }}>
                        {formatPercent(wallet.ytd_change_percent)}
                      </td>
                      <td className={`bb-table-cell text-right mono-number ${wallet.twr_total >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`} style={{ textAlign: 'right', paddingRight: '12px' }}>
                        {formatPercent(wallet.twr_total)}
                      </td>
                      <td className="bb-table-cell text-right mono-number" style={{ color: 'var(--bb-text-data)', textAlign: 'right', paddingRight: '12px' }}>
                        {wallet.volatility.toFixed(2)}%
                      </td>
                      <td className={`bb-table-cell text-right mono-number ${wallet.sharpe_ratio >= 0 ? 'bb-value-positive' : 'bb-value-negative'}`} style={{ textAlign: 'right', paddingRight: '12px' }}>
                        {wallet.sharpe_ratio.toFixed(2)}
                      </td>
                      <td className="bb-table-cell text-center" style={{ textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 6px',
                          fontSize: '10px',
                          backgroundColor: wallet.status === 'active' ? 'rgba(0, 255, 0, 0.2)' :
                                         wallet.status === 'alert' ? 'rgba(255, 0, 0, 0.2)' :
                                         'rgba(128, 128, 128, 0.2)',
                          color: wallet.status === 'active' ? 'var(--bb-green)' :
                                wallet.status === 'alert' ? 'var(--bb-red)' :
                                'var(--bb-gray)',
                          border: `1px solid ${wallet.status === 'active' ? 'var(--bb-green)' :
                                              wallet.status === 'alert' ? 'var(--bb-red)' :
                                              'var(--bb-gray)'}`
                        }}>
                          {wallet.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="bb-table-cell text-center" style={{ textAlign: 'center' }}>
                        <button
                          className="bb-button"
                          style={{ padding: '2px 6px', fontSize: '10px' }}
                          onClick={() => onViewWallet?.(wallet.id)}
                        >
                          VIEW
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {wallets.length > displayCount && (
              <div style={{
                padding: '10px',
                textAlign: 'center',
                borderTop: '1px solid var(--bb-border)',
                fontSize: '11px',
                color: 'var(--bb-text-secondary)'
              }}>
                SHOWING {displayCount} OF {wallets.length} WALLETS
                <button
                  className="bb-button"
                  style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '10px' }}
                  onClick={() => setDisplayCount(displayCount + 100)}
                >
                  LOAD MORE
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PortfolioOverviewDashboard;